import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DimensionPreview } from "@/components/DimensionPreview";
import { useCart } from "@/context/CartContext";
import { brl, num, calcItem, priceSuffix, discountBadge } from "@/lib/api";

const Field = ({ id, label, ...props }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
    <Input id={id} data-testid={`input-${id}`} className="h-12 font-mono text-lg" {...props} />
  </div>
);

export function PriceCalculator({ product }) {
  const isM2 = product.pricing_type === "m2";
  const [height, setHeight] = useState(isM2 ? "100" : "");
  const [width, setWidth] = useState(isM2 ? "100" : "");
  const [quantity, setQuantity] = useState("1");
  const { add } = useCart();
  const navigate = useNavigate();
  const { area, billedArea, minApplied, subtotal } = calcItem(product.pricing_type, product.price, { width, height, quantity });
  const valid = Number(quantity) >= 1 && (!isM2 || (Number(width) > 0 && Number(height) > 0 && Number(width) <= 10000 && Number(height) <= 10000));

  const handleAdd = () => {
    add(product, { width, height, quantity });
    toast.success(`${product.name} adicionado ao carrinho`, { action: { label: "Ver carrinho", onClick: () => navigate("/carrinho") } });
  };

  return (
    <div className="space-y-5 rounded-md border border-slate-200 bg-white p-6 shadow-sm" data-testid="price-calculator">
      <div className="flex items-baseline justify-between">
        <p className="font-display text-sm font-semibold uppercase tracking-wider text-slate-500">{isM2 ? "Calcule pelo tamanho" : "Escolha a quantidade"}</p>
        <p className="font-mono text-sm font-bold text-slate-700" data-testid="calc-unit-price">
          {product.regular_price && <span className={`mr-2 whitespace-nowrap rounded-full px-2 py-0.5 font-sans text-[10px] ${discountBadge(product).cls}`} data-testid="calc-reseller-badge">{discountBadge(product).label}</span>}
          {product.regular_price && <span className="mr-1 text-xs font-medium text-slate-400 line-through">{brl(product.regular_price)}</span>}
          {brl(product.price)}{priceSuffix(product)}
        </p>
      </div>
      {isM2 && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <Field id="altura" label="Altura (cm)" type="number" step="0.1" min="0.1" max="10000" value={height} onChange={(e) => setHeight(e.target.value)} />
            <Field id="largura" label="Largura (cm)" type="number" step="0.1" min="0.1" max="10000" value={width} onChange={(e) => setWidth(e.target.value)} />
          </div>
          <DimensionPreview width={width} height={height} />
        </>
      )}
      <Field id="quantidade" label={isM2 ? "Quantidade de peças" : `Quantidade (${product.unit_label || "unidade"})`} type="number" min="1" step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
      <div className="space-y-2 rounded-md bg-slate-900 p-5 text-white">
        {isM2 && (
          <div className="flex justify-between text-sm text-slate-300">
            <span>Área por peça</span>
            <span className="font-mono" data-testid="calc-area">{num(area, 4)} m²</span>
          </div>
        )}
        {minApplied && (
          <div className="flex justify-between text-sm text-amber-300" data-testid="calc-min-area">
            <span>Área cobrada (mínimo)</span>
            <span className="font-mono">{num(billedArea, 2)} m²</span>
          </div>
        )}
        <div className="flex items-end justify-between">
          <span className="text-sm text-slate-300">Valor total</span>
          <span className="font-mono text-3xl font-bold text-[#38bdf8]" data-testid="calc-total">{brl(subtotal)}</span>
        </div>
      </div>
      {isM2 && <p className="text-xs text-slate-500" data-testid="calc-min-rule">Valor mínimo por peça: 0,5 m² ({brl(product.price * 0.5)}).</p>}
      <Button onClick={handleAdd} disabled={!valid} className="h-12 w-full rounded-full bg-[#E5007D] text-base font-bold hover:bg-[#c2006a]" data-testid="btn-adicionar-carrinho">
        <ShoppingBag className="h-5 w-5" /> Adicionar ao carrinho
      </Button>
    </div>
  );
}
